import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import styled from "styled-components";
import type { ICodeListFamily } from "types/AdjustmentRules/AdjustmentRules";

import {
  bucketDisplayName,
  bucketScopeOptions,
  codeListsDiffer,
  IBucketableRule,
  IBucketPair,
  IBucketScopeOption,
  IRuleBucketMember,
  membersInScope,
  pairKey,
  ruleCountLabel,
  TBucketScope,
  toBucketMembers,
  widestScope,
} from "../helpers/ruleBucket";

/**
 * CLE-2842 — "bucket mode" for the MPPR / Case Rate / Package edit pages.
 *
 * A bucket is every SCOPED row of the type that is the same rule — same code lists, same terms,
 * same enabled state — whatever its provider, payer or name. When the rule being edited is in a
 * bucket of two or more, the page offers a scope: "Only this payer" (the single-row save, as
 * before), "All payers of <provider>" and "All providers" (one atomic group save each), defaulting
 * to the widest. The provider section, the rule name, the contract language, the submit button and
 * the case-rate conflict check all read this context; for a lone rule `useRuleBucket()` is null
 * and they behave exactly as before.
 */
export interface IRuleBucket {
  groupKey: string;
  codeListKey: string | null;
  codeListFamily: ICodeListFamily;
  /** The row being edited, and its provider. */
  rowId: string;
  providerId: string;
  providerName: string;
  /** Every current row of the bucket, across providers, in the server's order. */
  members: IRuleBucketMember[];
  /** The scopes this bucket offers, narrowest first. */
  options: IBucketScopeOption[];
  scope: TBucketScope;
  /** Changing the scope drops any pending additions and removals, so they cannot come back unseen. */
  setScope: (scope: TBucketScope) => void;
  /** True for "All payers of <provider>" and "All providers": the save is one group request. */
  isGroupScope: boolean;
  /** The members the scope reaches, including any marked for removal. */
  scopeMembers: IRuleBucketMember[];
  /** Member ids to delete on save. */
  removedIds: string[];
  removeMember: (id: string) => void;
  /** New copies to create on save. */
  added: IBucketPair[];
  /** Adds a pair; re-adding a pair whose row was marked for removal just keeps that row. */
  addPair: (pair: IBucketPair) => void;
  removeAddedPair: (pair: {
    providerId: string;
    payerId: number | null;
  }) => void;
  /** Members in scope that survive the save. */
  activeMembers: IRuleBucketMember[];
  /** Every pair the edit will cover after the save: surviving members in scope + added pairs. */
  activePairs: IBucketPair[];
  /** The type's code-list fields and their values as loaded. */
  codeListFields: string[];
  loadedCodeLists: Record<string, unknown>;
  /** The "Also update this code list on N other rules" checkbox. */
  familyChecked: boolean;
  setFamilyChecked: (value: boolean) => void;
  /**
   * While a group save is in flight: how many rules it saves (null otherwise). A large save takes
   * many seconds, so the page is blocked — no second submit, no leaving — until it resolves.
   */
  savingCount: number | null;
  /** Shows (a count) or clears (null) the blocking "Saving N rules…" state. */
  markSaving: (count: number | null) => void;
  /**
   * Takes the one-save-at-a-time lock, from the confirmation until the request resolves; false when
   * it is already held. Synchronous (a ref, not state), so two submits in the same tick cannot both
   * get through.
   */
  lockSave: () => boolean;
  /** Releases the lock: the confirmation was cancelled or closed, or the save failed. */
  releaseSave: () => void;
  /** This rule's own name — what "Only this payer" edits. */
  ownName: string;
  /**
   * The display name of the members in scope (longest distinct name): what the Rule Name field
   * starts from in a group scope. A group save renames only when the field differs from it.
   */
  scopeDisplayName: string;
}

const RuleBucketContext = createContext<IRuleBucket | null>(null);

export function useRuleBucket(): IRuleBucket | null {
  return useContext(RuleBucketContext);
}

const NO_FAMILY: ICodeListFamily = { otherRuleCount: 0, otherVariantCount: 0 };

const SavingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
`;

const SavingCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  font-weight: 600;
`;

interface IRuleBucketProviderProps {
  /** The row being edited, as loaded by id — its `bucketMembers` define the bucket. */
  row: IBucketableRule | undefined;
  /** The type's code-list fields (see `CODE_LIST_FIELDS`). */
  codeListFields?: string[];
  children: ReactNode;
}

const memberToPair = (member: IRuleBucketMember): IBucketPair => ({
  providerId: member.providerId,
  providerName: member.providerName,
  payerId: member.payerId,
  payerName: member.payerName,
});

export function RuleBucketProvider({
  row,
  codeListFields = [],
  children,
}: IRuleBucketProviderProps) {
  const members = useMemo(() => toBucketMembers(row), [row]);
  const options = useMemo(
    () => bucketScopeOptions(members, row?.id),
    [members, row?.id]
  );
  // Null until the user picks one: the widest option is the default.
  const [chosenScope, setChosenScope] = useState<TBucketScope | null>(null);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [added, setAdded] = useState<IBucketPair[]>([]);
  const [familyChecked, setFamilyChecked] = useState(true);
  const [savingCount, setSavingCount] = useState<number | null>(null);
  const saveLockRef = useRef(false);
  const lockSave = useCallback(() => {
    if (saveLockRef.current) return false;
    saveLockRef.current = true;
    return true;
  }, []);
  const releaseSave = useCallback(() => {
    saveLockRef.current = false;
  }, []);

  const setScope = useCallback((next: TBucketScope) => {
    setChosenScope(next);
    setRemovedIds([]);
    setAdded([]);
  }, []);

  const fieldsKey = codeListFields.join(",");

  const value = useMemo<IRuleBucket | null>(() => {
    if (!row?.groupKey || members.length <= 1) return null;
    const self = members.find((member) => member.id === row.id) ?? members[0];
    const scope =
      chosenScope && options.some((option) => option.value === chosenScope)
        ? chosenScope
        : widestScope(options);
    const scopeMembers = membersInScope(members, scope, self.providerId);
    const activeMembers = scopeMembers.filter(
      (member) => !removedIds.includes(member.id)
    );
    const fields = fieldsKey ? fieldsKey.split(",") : [];
    return {
      groupKey: row.groupKey,
      codeListKey: row.codeListKey ?? null,
      codeListFamily: row.codeListFamily ?? NO_FAMILY,
      rowId: self.id,
      providerId: self.providerId,
      providerName: self.providerName,
      members,
      options,
      scope,
      setScope,
      isGroupScope: scope !== "PAYER",
      scopeMembers,
      removedIds,
      removeMember: (id: string) =>
        setRemovedIds((ids) => (ids.includes(id) ? ids : [...ids, id])),
      added,
      addPair: (pair: IBucketPair) => {
        const removedMember = scopeMembers.find(
          (member) =>
            pairKey(member) === pairKey(pair) && removedIds.includes(member.id)
        );
        if (removedMember) {
          setRemovedIds((ids) => ids.filter((id) => id !== removedMember.id));
          return;
        }
        const taken = new Set([
          ...activeMembers.map(pairKey),
          ...added.map(pairKey),
        ]);
        if (taken.has(pairKey(pair))) return;
        setAdded((pairs) => [...pairs, pair]);
      },
      removeAddedPair: (pair) =>
        setAdded((pairs) =>
          pairs.filter((existing) => pairKey(existing) !== pairKey(pair))
        ),
      activeMembers,
      activePairs: [...activeMembers.map(memberToPair), ...added],
      codeListFields: fields,
      loadedCodeLists: Object.fromEntries(
        fields.map((field) => [
          field,
          (row as unknown as Record<string, unknown>)[field] ?? [],
        ])
      ),
      familyChecked,
      setFamilyChecked,
      savingCount,
      markSaving: setSavingCount,
      lockSave,
      releaseSave,
      ownName: String(
        (row as unknown as { groupName?: string | null }).groupName ?? ""
      ),
      scopeDisplayName: bucketDisplayName(
        scopeMembers.map((member) => member.groupName)
      ),
    };
  }, [
    row,
    members,
    options,
    chosenScope,
    setScope,
    removedIds,
    added,
    familyChecked,
    savingCount,
    lockSave,
    releaseSave,
    fieldsKey,
  ]);

  return (
    <RuleBucketContext.Provider value={value}>
      {children}
      {value && savingCount !== null && (
        <SavingOverlay
          role="alert"
          aria-busy="true"
          data-testid="bucket-saving"
        >
          <SavingCard>
            <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            {`Saving ${ruleCountLabel(
              savingCount
            )}… large rules can take up to a minute.`}
          </SavingCard>
        </SavingOverlay>
      )}
    </RuleBucketContext.Provider>
  );
}

/**
 * CLE-2842 Part 3 — the code-list family offer. Shown when a group save edits the code lists and
 * other rules use the same list: ticked, the save also rewrites the code list of every SCOPED rule
 * of the type that used it and is not in the save's `ids` — the bucket's own members outside the
 * scope (e.g. the other providers' copies when the scope is "All payers of <provider>") and the
 * rules that use the list with different terms (`codeListFamily`, e.g. the same 4,183 codes with
 * four discount sets) — and nothing else of theirs. Only in "All payers of <provider>" and "All
 * providers".
 */
export function useCodeListFamily() {
  const bucket = useRuleBucket();
  const { control } = useFormContext<any>();
  const fields = bucket?.codeListFields ?? [];
  const watched = useWatch({ control, name: fields }) as unknown[];
  const current = Object.fromEntries(
    fields.map((field, index) => [field, watched?.[index]])
  );
  const family = bucket?.codeListFamily ?? NO_FAMILY;
  // Every rule the server rewrites: bucket members outside the scope + the other-terms variants.
  const outsideScope = bucket
    ? bucket.members.length - bucket.scopeMembers.length
    : 0;
  const familyCount = outsideScope + family.otherRuleCount;
  const eligible =
    !!bucket &&
    bucket.isGroupScope &&
    !!bucket.codeListKey &&
    familyCount > 0 &&
    codeListsDiffer(current, bucket.loadedCodeLists, fields);
  return {
    eligible,
    checked: !!bucket?.familyChecked,
    setChecked: (value: boolean) => bucket?.setFamilyChecked(value),
    /** Whether the save sends `alsoUpdateCodeListFamily`. */
    willUpdate: eligible && !!bucket?.familyChecked,
    /** How many other rules the family rewrite reaches. */
    familyCount,
    /** A group save is in flight. */
    saving: bucket?.savingCount != null,
    /** Of those, the rules that use the list with different terms. */
    otherRuleCount: family.otherRuleCount,
    otherVariantCount: family.otherVariantCount,
  };
}
